import { cloneElement, useState, useMemo, useEffect, useRef, ReactNode, useCallback } from 'react'
import { Button, Banner, classNames } from '@panwds/react-ui'
import { events } from '@sparky/framework'
import { ACCORDION_COMPLETE_EVENT_KEY } from '../../constants'
import { getVarValue } from '../../../common/utils/Sparky'
import { FF_UA_GENERIC_DISABLE } from '../../../common/utils/FeatureFlags'

import type { ComponentDef } from '../../renderer'
import type { ReactElement, VFC } from 'react'
import type { BannerAppearance } from '@panwds/react-ui'
import type { CommonStoreState } from '../../store'


export type UAccordionProps = {
  title?: any
  children: ComponentDef | ComponentDef[]
  hasDoneButton?: boolean
  state: CommonStoreState
  componentKey: string
}

export type BannerProps = {
  display: boolean
  message?: string | ReactElement
  appearance?: BannerAppearance
}
const defaultBannerProps: BannerProps = {
  display: false,
  message: 'We have updated this selection based on your tenant selection.',
  appearance: 'warning' as BannerAppearance
}

const genericDisable = getVarValue(FF_UA_GENERIC_DISABLE, false)
/**
 * This UAccordion component has 2 basic state, collapsed and expanded.
 * The direct children of this component needs to be aware of below props:
 * 1. updateTitle, which will define the accordion display in collapsed mode, it takes HTMLElement
 * 2. setAccordionOpen, which will set the accordion state by pass a boolean
 * 3. setDisable, deprecated, don't use. which will render a half transparent overlay on top to disable the click, takes boolean
 * 4. setEditDisable, whic will disable the Edit button on collapsed mode, take boolean
 * 5. setBanner, which will render a banner in collapsed mode, requires BannerProps
 * 6. completeAccordion, required to implement, this will tell the form this accordion is complete and form will caculate the next one
 * If the children component returns null, this component will also be hidden
 * @param props
 * @returns
 */

const UAccordion: VFC<UAccordionProps> = (props) => {
  const { title: defaultTitle, children, hasDoneButton = false, state, componentKey } = props
  const { activationSteps } = state

  const [isAccordionOpen, setAccordionOpen] = useState<boolean>(true)
  const [dynamicTitle, setTitle] = useState<HTMLElement | undefined>(undefined)
  const [disabled, setDisable] = useState<boolean>(false)
  const [banner, setBanner] = useState<BannerProps>(defaultBannerProps)
  const [hideSelf, setHideSelf] = useState<boolean>(false)
  const [editDisabled, setEditDisable] = useState<boolean>(false)

  const wrapperRef = useRef<HTMLDivElement>(null)

  /**
   * disable or enable current accordion based on activationSteps
   */
  useEffect(() => {
    if (!genericDisable) {
      return
    }
    if (!activationSteps || !activationSteps[componentKey]) {
      return
    }
    if (activationSteps[componentKey].isAccordionEnabled) {
      setDisable(false)
    }
    else {
      setDisable(true)
    }
  }, [activationSteps, componentKey])

  const updateTitle = useCallback((value: HTMLElement | undefined) => {
    setTitle(value)
  }, [setTitle])

  const renderChildInformation = useCallback((children: any) => {
    return children?.props?.children.map((child: any) => {
      const passProps = {
        updateTitle,
        setAccordionOpen,
        setDisable: genericDisable ? () => { } : setDisable,
        setEditDisable,
        setBanner,
        completeAccordion: genericDisable ? () => {
          events.emit(ACCORDION_COMPLETE_EVENT_KEY, componentKey)
        } : () => {}
      }
      return cloneElement(child, { ...child.props, ...passProps })
    })
  }, [componentKey, updateTitle])

  // listen and update hideSelf
  const ref = useRef(hideSelf)
  ref.current = hideSelf
  useEffect(() => {
    // this will need the child component to update state, so accordion can listen to the change
    const currentHideSelf = !wrapperRef?.current?.hasChildNodes()
    if (ref.current !== currentHideSelf) {
      setHideSelf(currentHideSelf)
    }
  }, [state])

  // this is the method to listen DOM change, will work without listen state, but too heavy for this usecase
  // useEffect(() => {
  //   setHideSelf(!wrapperRef?.current?.hasChildNodes())
  //   const config = { childList: true }
  //   let hidden = !wrapperRef?.current?.hasChildNodes()
  //   // Callback function to execute when mutations are observed
  //   const callback = (mutationList: any, observer: any) => {
  //     for (const mutation of mutationList) {
  //       if (mutation.type === 'childList') {
  //         const currentHidden = !wrapperRef?.current?.hasChildNodes()
  //         if (hidden !== currentHidden) {
  //           console.log('A child node has been added or removed.')
  //           setHideSelf(currentHidden)
  //           hidden = currentHidden
  //         }
  //       }
  //     }
  //   }
  //   if (wrapperRef.current) {
  //     const observer = new MutationObserver(callback)
  //     observer.observe(wrapperRef.current, config)
  //     return () => {
  //       observer.disconnect()
  //     }
  //   }
  //   else {
  //     return
  //   }
  // }, [])

  const Overlay = () => (
    <div className='cm-absolute cm-t-0 cm-l-0 cm-w-full cm-h-full cm-opacity-50 cm-bg-white dark:cm-bg-black cm-z-30' ></div>
  )
  const onBannerClose = () => {
    setBanner((prevState: BannerProps) => {
      return { ...prevState, display: false }
    })
  }
  const title = useMemo(() => {
    return (
      <div className='cm-relative'>
        <Button
          addClassName='!cm-absolute cm-right-0 cm-top-0'
          appearance='clear'
          size='sm'
          disabled={editDisabled}
          onClick={() => {
            setAccordionOpen(true)
          }}
        >Edit</Button>
        {dynamicTitle ?? defaultTitle}
        {banner.display &&
          <div className='cm-mt-4'>
            <Banner
              type='inline'
              showIcon
              appearance={banner.appearance ?? defaultBannerProps.appearance}
              onClose={onBannerClose}
            >
              {banner.message ?? defaultBannerProps.message}
            </Banner>
          </div>}
      </div >)
  }, [banner, defaultTitle, dynamicTitle, editDisabled])

  const accordionClassNames = useMemo(() => {
    return classNames('cm-border-solid cm-border cm-rounded-md cm-mb-3 cm-p-4 cm-border-[#DADBDB] dark:cm-border-[#242424] hover:cm-border-[#38A5FF]',
      hideSelf ? 'cm-hidden' : '',
    )
  }, [hideSelf])
  const outerContainerClassName = useMemo(() => {
    return classNames('cm-relative',
      hideSelf ? 'cm-hidden' : '',
    )
  }, [hideSelf])
  return (
    <div className={outerContainerClassName}>
      {disabled && <Overlay />}
      <div className={accordionClassNames}>
        {!isAccordionOpen && title}
        <Collapse isOpen={isAccordionOpen}>
          <div className='cm-relative'>
            {hasDoneButton &&
              <Button
                addClassName='!cm-absolute cm-right-0 cm-top-0'
                appearance='primary'
                onClick={() => {
                  setAccordionOpen(false)
                  // events.emit(ACCORDION_COMPLETE_EVENT_KEY, componentKey)
                }}
              >Done</Button>
            }
            <div ref={wrapperRef}>
              {renderChildInformation(children)}
            </div>
          </div>
        </Collapse>
      </div>
    </div>
  )
}

export default UAccordion

interface CollapseProps {
  isOpen: boolean;
  duration?: number;
  children: ReactNode;
}

const Collapse = (props: CollapseProps) => {
  const { isOpen: _isOpen, duration = 300, children } = props
  const outerRef = useRef<HTMLDivElement>(null)
  const content = useRef<HTMLDivElement>(null)
  const [height, setHeight] = useState<string | undefined>(undefined)
  const [isOpen, setIsOpen] = useState<boolean>(_isOpen)

  const clientHeight = content?.current?.clientHeight
  useEffect(() => {
    if (outerRef && outerRef.current) {
      outerRef.current.ontransitionend = () => {
        if (isOpen) {
          setHeight(undefined)
        }
      }
    }
    if (clientHeight) {
      setHeight(`${clientHeight}px`)
    }
  }, [clientHeight, isOpen])

  useEffect(() => {
    if (!_isOpen && !height && clientHeight) {
      setHeight(`${clientHeight}px`)
      return
    }
    setIsOpen(_isOpen)
  }, [_isOpen, height, clientHeight])

  return (
    <div
      ref={outerRef}
      className={`cm-transition-all cm-overflow-hidden cm-duration-${duration} cm-ease-out`.trim()}
      style={{ maxHeight: isOpen ? height : 0 }}>
      <div ref={content}>{children}</div>
    </div>
  )
}
